/**
 * One saved media plan (?plan=<id>). Top to bottom: who and what, one
 * budget-option picker that drives both the numbers below and "Download
 * client PDF", the status and who may change it, the plan itself, how it was
 * priced (internal), the client files and whose Drive each Sheet is in, and a
 * Details disclosure (ids, versions, status history). "New version" and
 * "Save these rates for {advertiser}" start here.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CopyIcon, IconButton, Spinner, StatusPill, Tabs } from '../../ui';
import InlineConfirm from './InlineConfirm';
import PlanLinesTable from './PlanLinesTable';
import PlanPricing from './PlanPricing';
import PlanStatusPill from './PlanStatusPill';
import PlanTotals from './PlanTotals';
import { connectGoogleWorkspace } from './googleWorkspace';
import WarningsBox from './WarningsBox';
import type { Notify } from './PlanBuilder';
import {
  GoogleReconnectRequiredError,
  downloadAuthedFile,
  downloadMediaPlanPdf,
  exportMediaPlanSheet,
  generateMediaPlanPdf,
  getMediaPlan,
  saveMediaPlanAsRateCard,
  setMediaPlanStatus,
  type GeneratedPdf,
} from '../../../services/mediaPlansApi';
import type { CreatePlanResult, MediaPlanDoc, MediaPlanExport, MediaPlanUnitDef } from './types';
import { PLAN_STATUS_LABELS } from './types';
import { canMakeNewVersion, planOpenChecks, statusMovesFor, STATUS_MEANING, type SettableStatus, type StatusMove } from './planStatus';
import { PLAN_SOURCE_LABELS } from './vocab';
import { bannerStyle, fmtBudget, fmtDate, fmtDateTime, mutedText, sectionCardStyle, sectionTitleStyle } from './formStyles';

export interface PlanDetailSeed {
  plan: MediaPlanDoc;
  agencyLabel?: string;
  sheet?: CreatePlanResult['sheet'];
  sheetError?: CreatePlanResult['sheetError'];
}

interface Props {
  planId: string;
  /** Result of a creation that just happened — skips the fetch and shows the outcome banner. */
  seed?: PlanDetailSeed | null;
  units: MediaPlanUnitDef[];
  canManagePlans: boolean;
  /** The signed-in user's uid: the creator may change the status too. */
  currentUid: string | null;
  onBack: () => void;
  onOpenPlan: (id: string) => void;
  onNewVersion: (plan: MediaPlanDoc, agencyLabel?: string) => void;
  onPlanChanged?: (plan: MediaPlanDoc) => void;
  notify: Notify;
}

type Confirming = { kind: 'status'; move: StatusMove } | { kind: 'save-rates' } | null;

const fmtBytes = (n: number): string => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

const EXPORT_LABELS: Record<MediaPlanExport['kind'], string> = {
  google_sheet: 'Google Sheet',
  xlsx: 'Excel',
  agency_template: 'Agency template',
  pdf: 'Client PDF',
};

const PlanDetail: React.FC<Props> = ({ planId, seed, units, canManagePlans, currentUid, onBack, onOpenPlan, onNewVersion, onPlanChanged, notify }) => {
  const seeded = seed && seed.plan.id === planId ? seed : null;
  const [plan, setPlan] = useState<MediaPlanDoc | null>(seeded?.plan || null);
  const [routeAgencyLabel, setRouteAgencyLabel] = useState<string | undefined>(seeded?.agencyLabel);
  const [loading, setLoading] = useState(!seeded);
  const [error, setError] = useState<string | null>(null);
  const [sheetState, setSheetState] = useState<{ kind: 'created' | 'reconnect' | 'failed'; message?: string } | null>(
    seeded ? (seeded.sheet ? { kind: 'created' } : seeded.sheetError ? { kind: seeded.sheetError.code === 'google_reconnect_required' ? 'reconnect' : 'failed', message: seeded.sheetError.message } : null) : null,
  );
  const [exporting, setExporting] = useState(false);
  const [tierIndex, setTierIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [lastPdf, setLastPdf] = useState<GeneratedPdf | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [saving, setSaving] = useState(false);

  // Every fresh copy of the plan also reaches the list, so its status and client files stay current.
  const onPlanChangedRef = useRef(onPlanChanged);
  useEffect(() => {
    onPlanChangedRef.current = onPlanChanged;
  });
  const applyPlan = useCallback((next: MediaPlanDoc) => {
    setPlan(next);
    onPlanChangedRef.current?.(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMediaPlan(planId);
      applyPlan(result.plan);
      setRouteAgencyLabel(result.agencyLabel);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [planId, applyPlan]);

  // A seed is used once, at mount; later visits fetch.
  const [usedSeed] = useState(!!seeded);
  useEffect(() => {
    if (!usedSeed) void load();
  }, [usedSeed, load]);

  const sheet = useMemo(() => (plan ? [...plan.exports].reverse().find((e) => e.kind === 'google_sheet') : undefined), [plan]);

  const copy = (text: string, what: string) => {
    if (!navigator.clipboard) {
      notify('error', 'Copy is not available in this browser');
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => notify('success', `${what} copied`),
      () => notify('error', `Could not copy the ${what.toLowerCase()}`),
    );
  };

  const createSheet = async () => {
    if (!plan) return;
    setExporting(true);
    try {
      await exportMediaPlanSheet(plan.id);
      setSheetState({ kind: 'created' });
      await load();
      notify('success', 'Google Sheet created in your Drive');
    } catch (err) {
      if (err instanceof GoogleReconnectRequiredError) setSheetState({ kind: 'reconnect', message: err.message });
      else setSheetState({ kind: 'failed', message: errorMessage(err) });
    } finally {
      setExporting(false);
    }
  };

  const downloadPdf = async () => {
    if (!plan) return;
    setGenerating(true);
    try {
      const generated = await generateMediaPlanPdf(plan.id, { tierIndex });
      setLastPdf(generated);
      await downloadMediaPlanPdf(generated);
      notify('success', 'Client PDF downloaded', `${generated.pdf.fileName} · ${generated.pdf.pageCount} page${generated.pdf.pageCount === 1 ? '' : 's'}`);
      void load();
    } catch (err) {
      notify('error', 'Could not make the client PDF', errorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const changeStatus = async (to: SettableStatus) => {
    if (!plan) return;
    setSaving(true);
    try {
      applyPlan(await setMediaPlanStatus(plan.id, to));
      setConfirming(null);
      notify('success', `Marked ${PLAN_STATUS_LABELS[to].toLowerCase()}`);
    } catch (err) {
      notify('error', 'Could not change the status', errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveRates = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const card = await saveMediaPlanAsRateCard(plan.id, { activate: true });
      setConfirming(null);
      notify('success', `Rates saved for ${plan.advertiser.name}`, card.name);
    } catch (err) {
      notify('error', 'Could not save the rates', errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !plan) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Spinner label="Loading media plan…" />
      </div>
    );
  }
  if (!plan) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button type="button" onClick={onBack} style={styles.backLink}>
          ← All media plans
        </button>
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>{error || 'This media plan could not be loaded.'}</span>
          <Button size="sm" variant="ghost" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const req = plan.request;
  const tiers = plan.tiers;
  const tier = tiers[Math.min(tierIndex, tiers.length - 1)];
  const superseded = plan.status === 'superseded';
  // An approved plan keeps its status while its next version is drafted.
  const revisionPending = !superseded && !!plan.supersededByPlanId;
  const canRevise = canMakeNewVersion(plan, { uid: currentUid, canManage: canManagePlans });
  const agencyLabel = plan.agencyLabel || routeAgencyLabel;
  const isCreator = !!currentUid && plan.createdBy.uid === currentUid;
  const moves = superseded ? [] : statusMovesFor(plan.status, { isCreator, canManage: canManagePlans });
  const checks = planOpenChecks(plan);
  const creatorName = plan.createdBy.name || plan.createdBy.email;
  const sourceWords = PLAN_SOURCE_LABELS[plan.source];
  const tierLabel = (i: number | undefined) => (typeof i === 'number' && tiers[i] && tiers.length > 1 ? `${fmtBudget(tiers[i].budgetUsd)} option` : '');
  const driveOwner = (e: MediaPlanExport): string | null => {
    if (e.kind !== 'google_sheet' || !e.createdBy) return null;
    return e.createdBy.uid === currentUid ? 'In your Drive' : `In ${e.createdBy.email}’s Drive`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button type="button" onClick={onBack} style={styles.backLink}>
        ← All media plans
      </button>

      {error && (
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>Could not refresh this plan: {error}</span>
          <Button size="sm" variant="ghost" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      )}
      {sheetState?.kind === 'created' && sheet && (
        <div role="status" style={bannerStyle('success')}>
          <span>Google Sheet ready.</span>
          <a href={sheet.url} target="_blank" rel="noopener noreferrer" style={styles.bannerLink}>
            Open the Google Sheet ↗
          </a>
        </div>
      )}
      {sheetState?.kind === 'reconnect' && (
        <div role="alert" style={bannerStyle('warning')}>
          <span style={{ flex: 1 }}>The plan is saved, but the Google Sheet needs your Google Workspace connection. {sheetState.message}</span>
          <Button size="sm" variant="subtle" onClick={connectGoogleWorkspace}>
            Connect Google Workspace
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void createSheet()} disabled={exporting}>
            {exporting ? 'Creating…' : 'Retry'}
          </Button>
        </div>
      )}
      {sheetState?.kind === 'failed' && (
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>The plan is saved, but the Google Sheet could not be created: {sheetState.message}</span>
          <Button size="sm" variant="ghost" onClick={() => void createSheet()} disabled={exporting}>
            {exporting ? 'Creating…' : 'Retry'}
          </Button>
        </div>
      )}
      {superseded && (
        <div style={bannerStyle('info')}>
          <span style={{ flex: 1 }}>{plan.supersededByPlanId ? `Replaced by v${plan.version + 1}.` : 'A newer version replaced this one.'} This version can’t change any more.</span>
          {plan.supersededByPlanId && (
            <Button size="sm" variant="subtle" onClick={() => onOpenPlan(plan.supersededByPlanId!)}>
              Open v{plan.version + 1} →
            </Button>
          )}
        </div>
      )}
      {revisionPending && (
        <div style={bannerStyle('info')}>
          <span style={{ flex: 1 }}>
            v{plan.version + 1} is in progress.{plan.status === 'approved' ? ` This version stays approved until v${plan.version + 1} is approved.` : ''}
          </span>
          <Button size="sm" variant="subtle" onClick={() => onOpenPlan(plan.supersededByPlanId!)}>
            Open v{plan.version + 1} →
          </Button>
        </div>
      )}

      <Card style={sectionCardStyle}>
        <div style={styles.headRow}>
          <div style={{ minWidth: 0 }}>
            <h2 style={styles.h2}>
              {plan.advertiser.name}
              <span style={{ ...mutedText, fontSize: 14, marginLeft: 8 }}>v{plan.version}</span>
            </h2>
            <div style={styles.meta}>
              <PlanStatusPill status={plan.status} />
              <span>{plan.advertiser.categoryLabel}</span>
              {plan.advertiser.categoryStatus === 'suggested' && (
                <StatusPill variant="pending" size="sm" noDot>
                  Unconfirmed
                </StatusPill>
              )}
              {agencyLabel && <span>via {agencyLabel}</span>}
              {req.objective && <span>· {req.objective}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-1)' }}>{tiers.map((t) => fmtBudget(t.budgetUsd)).join(' / ')}</div>
            <div style={mutedText}>
              {fmtDate(req.flightStart)} – {fmtDate(req.flightEnd)}
            </div>
          </div>
        </div>

        {tiers.length > 1 && (
          <div style={styles.actionGroup}>
            <span style={styles.inlineLabel}>Budget option</span>
            <Tabs value={String(tierIndex)} onChange={(v) => setTierIndex(Number(v))} options={tiers.map((t, i) => ({ value: String(i), label: fmtBudget(t.budgetUsd) }))} size="sm" ariaLabel="Budget option" />
          </div>
        )}

        <div style={styles.actions}>
          <Button variant="primary" size="sm" onClick={() => void downloadPdf()} disabled={generating}>
            {generating ? 'Making the PDF…' : tiers.length > 1 ? `Download client PDF · ${fmtBudget(tier.budgetUsd)}` : 'Download client PDF'}
          </Button>
          {lastPdf && !generating && (
            <Button variant="ghost" size="sm" onClick={() => void downloadMediaPlanPdf(lastPdf).catch((err) => notify('error', 'Download failed', errorMessage(err)))}>
              Download again · {lastPdf.pdf.pageCount} pg · {fmtBytes(lastPdf.pdf.sizeBytes)}
            </Button>
          )}
          {sheet ? (
            <Button variant="subtle" size="sm" onClick={() => window.open(sheet.url, '_blank', 'noopener')}>
              Open Google Sheet ↗
            </Button>
          ) : (
            <Button variant="subtle" size="sm" onClick={() => void createSheet()} disabled={exporting}>
              {exporting ? 'Creating the Sheet…' : 'Create a Sheet in my Drive'}
            </Button>
          )}
          <div style={{ ...styles.actionGroup, marginLeft: 'auto' }}>
            {canRevise && (
              <Button variant="subtle" size="sm" onClick={() => onNewVersion(plan, agencyLabel)}>
                New version
              </Button>
            )}
            {canManagePlans && plan.status === 'approved' && (
              <Button variant="ghost" size="sm" onClick={() => setConfirming({ kind: 'save-rates' })} disabled={saving}>
                Save these rates for {plan.advertiser.name}
              </Button>
            )}
          </div>
        </div>
        {confirming?.kind === 'save-rates' && (
          <InlineConfirm
            title={`Save these rates for ${plan.advertiser.name}?`}
            confirmLabel="Save rates"
            onConfirm={() => void saveRates()}
            onCancel={() => setConfirming(null)}
            busy={saving}
          >
            Future quotes for {plan.advertiser.name} will start from this plan’s rates (a negotiated rate card). Plans already saved keep their prices.
          </InlineConfirm>
        )}
      </Card>

      <Card style={sectionCardStyle}>
        <h3 style={sectionTitleStyle}>Status</h3>
        <div style={styles.actionGroup}>
          <PlanStatusPill status={plan.status} size="md" />
          <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>{STATUS_MEANING[plan.status]}</span>
        </div>
        {moves.length > 0 && (
          <div style={styles.actionGroup}>
            {moves.map((m) => (
              <Button
                key={m.to}
                size="sm"
                variant={m.to === 'approved' ? 'primary' : 'subtle'}
                disabled={saving}
                onClick={() => (m.confirm ? setConfirming({ kind: 'status', move: m }) : void changeStatus(m.to))}
              >
                {m.label}
              </Button>
            ))}
          </div>
        )}
        {!superseded && moves.length === 0 && (
          <div style={mutedText}>
            {plan.status === 'approved' ? 'Only a Media Plans manager can reopen an approved plan.' : `Only ${creatorName} or a Media Plans manager can change the status.`}
          </div>
        )}
        {confirming?.kind === 'status' && (
          <InlineConfirm
            title={confirming.move.to === 'approved' ? `Mark v${plan.version} approved?` : `Reopen v${plan.version} as shared?`}
            confirmLabel={confirming.move.label}
            onConfirm={() => void changeStatus(confirming.move.to)}
            onCancel={() => setConfirming(null)}
            busy={saving}
          >
            {confirming.move.to === 'approved' ? (
              checks.length === 0 ? (
                'No open checks on this plan.'
              ) : (
                <>
                  Before you do, check {checks.length === 1 ? 'this' : `these ${checks.length}`}:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {checks.map((c) => (
                      <li key={c.id}>{c.text}</li>
                    ))}
                  </ul>
                </>
              )
            ) : (
              'It goes back to waiting on the client.'
            )}
          </InlineConfirm>
        )}
      </Card>

      <Card style={sectionCardStyle}>
        <h3 style={sectionTitleStyle}>Plan{tiers.length > 1 ? ` · ${fmtBudget(tier.budgetUsd)} option` : ''}</h3>
        <PlanTotals tier={tier} request={req} />
        <PlanLinesTable tier={tier} request={req} onCopyPlacement={(name) => copy(name, 'Placement name')} />
        <WarningsBox warnings={Array.from(new Set([...plan.warnings, ...tier.warnings]))} />
      </Card>

      <Card style={sectionCardStyle}>
        <div>
          <h3 style={sectionTitleStyle}>How it was priced</h3>
          <div style={{ ...mutedText, marginTop: 4 }}>Internal — none of this appears on the client PDF or Sheet.</div>
        </div>
        <PlanPricing plan={plan} units={units} />
      </Card>

      <Card style={sectionCardStyle}>
        <h3 style={sectionTitleStyle}>Client files</h3>
        {plan.exports.length === 0 ? (
          <div style={mutedText}>None yet. Download the client PDF or create a Sheet above.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...plan.exports].reverse().map((e, i) => (
              <div key={`${e.fileId}-${i}`} style={styles.exportRow}>
                <span style={{ fontWeight: 600, minWidth: 96 }}>{EXPORT_LABELS[e.kind] || e.kind}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {e.kind === 'pdf' ? (
                    <button type="button" style={styles.inlineLink} onClick={() => void downloadAuthedFile(e.url, e.name).catch((err) => notify('error', 'Download failed', errorMessage(err)))}>
                      {e.name} ⤓
                    </button>
                  ) : (
                    <a href={e.url} target="_blank" rel="noopener noreferrer" style={styles.inlineLink}>
                      {e.name} ↗
                    </a>
                  )}
                  {tierLabel(e.tierIndex) && <span style={mutedText}> · {tierLabel(e.tierIndex)}</span>}
                </span>
                {driveOwner(e) && <span style={mutedText}>{driveOwner(e)}</span>}
                <span style={mutedText}>{fmtDate(e.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={mutedText}>A Sheet is created in the Drive of whoever makes it. PDFs are stored privately; anyone on the team can download them here.</div>
      </Card>

      <details style={styles.details}>
        <summary style={styles.summary}>Details</summary>
        <dl style={styles.dl}>
          <dt style={styles.dt}>Plan id</dt>
          <dd style={styles.dd}>
            <code>{plan.id}</code>
            <IconButton size="sm" variant="ghost" label="Copy plan id" onClick={() => copy(plan.id, 'Plan id')}>
              <CopyIcon />
            </IconButton>
          </dd>
          <dt style={styles.dt}>Versions</dt>
          <dd style={styles.dd}>
            v{plan.version}
            {plan.supersedesPlanId && (
              <>
                {' · revises '}
                <button type="button" style={styles.inlineLink} onClick={() => onOpenPlan(plan.supersedesPlanId!)}>
                  v{plan.version - 1}
                </button>
              </>
            )}
            {plan.supersededByPlanId && (
              <>
                {superseded ? ' · replaced by ' : ' · newer version '}
                <button type="button" style={styles.inlineLink} onClick={() => onOpenPlan(plan.supersededByPlanId!)}>
                  v{plan.version + 1}
                </button>
              </>
            )}
          </dd>
          <dt style={styles.dt}>Created</dt>
          <dd style={styles.dd}>
            {fmtDateTime(plan.createdAt)} by {plan.createdBy.name ? `${plan.createdBy.name} (${plan.createdBy.email})` : plan.createdBy.email}
            {sourceWords ? ` in ${sourceWords}` : ''}
          </dd>
          <dt style={styles.dt}>Updated</dt>
          <dd style={styles.dd}>
            {fmtDateTime(plan.updatedAt)}
            {plan.updatedBy ? ` by ${plan.updatedBy.email}` : ''}
          </dd>
          {plan.validThrough && (
            <>
              <dt style={styles.dt}>Rates valid through</dt>
              <dd style={styles.dd}>{fmtDate(plan.validThrough)}</dd>
            </>
          )}
          {plan.policyVersion && (
            <>
              <dt style={styles.dt}>Pricing rules</dt>
              <dd style={styles.dd}>
                {plan.policyVersion}
                {plan.historyStatus && plan.historyStatus !== 'ok' ? ` · history ${plan.historyStatus}` : ''}
              </dd>
            </>
          )}
          <dt style={styles.dt}>Status history</dt>
          <dd style={styles.dd}>
            {plan.statusHistory && plan.statusHistory.length > 0 ? (
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {plan.statusHistory.map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    {PLAN_STATUS_LABELS[h.from]} → {PLAN_STATUS_LABELS[h.to]} · {h.by.email} · {fmtDateTime(h.at)}
                    {h.viaPlanId ? ' (a newer version was approved)' : ''}
                  </li>
                ))}
              </ol>
            ) : plan.statusUpdatedBy ? (
              `${PLAN_STATUS_LABELS[plan.status]} since ${fmtDateTime(plan.statusUpdatedAt)} (set by ${plan.statusUpdatedBy.email})`
            ) : (
              'No changes since it was created.'
            )}
          </dd>
        </dl>
      </details>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backLink: { alignSelf: 'flex-start', border: 'none', background: 'transparent', color: 'var(--fg-link)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, fontFamily: 'var(--font-sans)' },
  bannerLink: { color: 'inherit', fontWeight: 700 },
  inlineLink: { border: 'none', background: 'transparent', color: 'var(--fg-link)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, padding: 0, fontFamily: 'var(--font-sans)', textDecoration: 'none' },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  h2: { margin: 0, fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--fg-1)' },
  meta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4, fontSize: 13, color: 'var(--fg-3)' },
  inlineLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-4)' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--border-subtle)' },
  actionGroup: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  exportRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '6px 10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface-2)', fontSize: 13 },
  details: { padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface-1)' },
  summary: { cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--fg-2)' },
  dl: { display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) 1fr', gap: '6px 16px', margin: '12px 0 4px', fontSize: 13 },
  dt: { color: 'var(--fg-4)', fontWeight: 600 },
  dd: { margin: 0, color: 'var(--fg-2)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0, overflowWrap: 'anywhere' },
};

export default PlanDetail;
